import { DropDownOptionResponse } from "@/Common/Api/UI/Dto/DropDownOptionResponse";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { TimeInterval } from "@/Common/Utilities/DateAndTime/TimeInterval";

class DropDownOption {
    public model: DropDownOptionResponse;

    constructor(dropdownResponse: DropDownOptionResponse) {
        this.model = dropdownResponse;
    }

    public numberValue(): number {
        return Number(this.model.Value);
    }

    public valueType(): string {
        return this.model.ValueType;
    }

    public getName(): string {
        return cacheStore.GetString(this.model.VocabularyResourceKey);
    }

    public getNameAbbr(): string {
        if (this.model.VocabularyResourceKeyAbbreviated != null)
            return cacheStore.GetString(this.model.VocabularyResourceKeyAbbreviated);
        else
            return this.getName();
    }

    public toComboboxItem(): ComboBoxDropDownItem {
        return new ComboBoxDropDownItem(this.model.Id, this.getName(), this.model.Position);
    }
}

class ScreeningTestFrequencyOption extends DropDownOption {
    public static getAll(): ScreeningTestFrequencyOption[] {
        const frequencyOptions = cacheStore.getDropDownOptionByType("ScreeningTestFrequencyType").map(p => new ScreeningTestFrequencyOption(p.model));

        return cacheStore.ClinicSettings.EnablePreventiveActivityEnhancements
            ? frequencyOptions
            : frequencyOptions.filter(p => p.model.Code !== "Every4Years");
    }

    public static getAllAsCombobox(): ComboBoxDropDownItem[] {
        return ScreeningTestFrequencyOption.getAll().map(x => x.toComboboxItem()).sortByProperty(x => x.Position);
    }

    public static fromDropDownId(id: string): ScreeningTestFrequencyOption {
        if (!id)
            return null;

        const item = ScreeningTestFrequencyOption.getAll().filter(x => x.model.Id == id).firstOrDefault();
        if (item)
            return new ScreeningTestFrequencyOption(item.model);

        return null;
    }

    public static fromDropDownOption(dropDown: DropDownOption): ScreeningTestFrequencyOption {
        if (!dropDown)
            return null;

        return new ScreeningTestFrequencyOption(dropDown.model);
    }

    public static getAllIntervals(): TimeInterval[] {
        return ScreeningTestFrequencyOption.getAll()
            .filter(frequencyOption => !frequencyOption.isOnceOnly())
            .sortByProperty(frequencyOption => frequencyOption.model.Position)
            .map(frequencyOption => frequencyOption.toTimeInterval());
    }

    public toTimeInterval(): TimeInterval {
        const value = this.numberValue();
        if (this.model.ValueType === "weeks")
            return TimeInterval.FromWeek(value);

        const years = value / 12;
        return years >= 1 && years % 1 === 0
            ? TimeInterval.FromYear(years)
            : TimeInterval.FromMonth(value);
    }

    public toMonthInterval(): TimeInterval {
        const value = this.numberValue();
        return TimeInterval.FromMonth(value);
    }

    public isOnceOnly(): boolean {
        return this.model.Code === "Once";
    }
}

class ScreeningTestReminderOption extends DropDownOption {
    public get interval(): number {
        return this.numberValue();
    }

    public get intervalType(): string {
        return this.valueType();
    }

    public static getAll(): ScreeningTestReminderOption[] {
        return cacheStore.getDropDownOptionByType("ScreeningTestReminderType").map(x => new ScreeningTestReminderOption(x.model));
    }

    public static getAllAsCombobox(): ComboBoxDropDownItem[] {
        return ScreeningTestReminderOption.getAll().map(x => x.toComboboxItem()).sortByProperty(x => x.Position);
    }

    public static fromDropDownId(id: string): ScreeningTestReminderOption {
        if (!id)
            return null;

        const item = ScreeningTestReminderOption.getAll().filter(x => x.model.Id == id).firstOrDefault();
        if (item)
            return new ScreeningTestReminderOption(item.model);

        return null;
    }

    public static fromDropDownOption(dropDown: DropDownOption): ScreeningTestReminderOption {
        if (!dropDown)
            return null;

        return new ScreeningTestReminderOption(dropDown.model);
    }
}

class ConsultationRequestPriorityOption extends DropDownOption {
    public static get Urgent(): ConsultationRequestPriorityOption {
        return ConsultationRequestPriorityOption.getAll().firstOrDefault(x => x.model.Code === "Urgent");
    }

    public static get Normal(): ConsultationRequestPriorityOption {
        return ConsultationRequestPriorityOption.getAll().firstOrDefault(x => x.model.Code === "Normal");
    }

    public static getAll(): ConsultationRequestPriorityOption[] {
        return cacheStore.getDropDownOptionByType("ConsultationRequestPriority").map(x => new ConsultationRequestPriorityOption(x.model));
    }

    public static getAllAsCombobox(): ComboBoxDropDownItem[] {
        return ConsultationRequestPriorityOption.getAll().map(x => x.toComboboxItem());
    }

    public static fromDropDownId(id: string): ConsultationRequestPriorityOption {
        if (!id)
            return null;

        const item = ConsultationRequestPriorityOption.getAll().firstOrDefault(x => x.model.Id == id);
        if (item)
            return new ConsultationRequestPriorityOption(item.model);

        return null;
    }

    public static fromDropDownOption(dropDown: DropDownOption): ConsultationRequestPriorityOption {
        if (!dropDown)
            return null;

        return new ConsultationRequestPriorityOption(dropDown.model);
    }
}

class ExamRequestPriorityOption extends DropDownOption {
    public static get Urgent(): ExamRequestPriorityOption {
        return ExamRequestPriorityOption.getAll().firstOrDefault(x => x.model.Code === "Urgent");
    }

    public static get Normal(): ConsultationRequestPriorityOption {
        return ExamRequestPriorityOption.getAll().firstOrDefault(x => x.model.Code === "Normal");
    }

    public static getAll(): ExamRequestPriorityOption[] {
        return cacheStore.getDropDownOptionByType("LabRequestPriority").map(x => new ExamRequestPriorityOption(x.model));
    }

    public static getAllAsCombobox(): ComboBoxDropDownItem[] {
        return ExamRequestPriorityOption.getAll().map(x => x.toComboboxItem());
    }

    public static fromDropDownId(id: string): ExamRequestPriorityOption {
        if (IsNullOrWhiteSpace(id))
            return null;

        const item = ExamRequestPriorityOption.getAll().firstOrDefault(x => x.model.Id == id);
        if (item)
            return new ExamRequestPriorityOption(item.model);

        return null;
    }

    public static fromDropDownOption(dropDown: DropDownOption): ExamRequestPriorityOption {
        if (!dropDown)
            return null;

        return new ExamRequestPriorityOption(dropDown.model);
    }
}

export { ConsultationRequestPriorityOption, DropDownOption, ExamRequestPriorityOption, ScreeningTestFrequencyOption, ScreeningTestReminderOption };
